/*
 * Copyright (c) 2023 NEXTWELL INTL SRL. All Rights Reserved.
 * This software is the proprietary information of NEXTWELL INTL SRL.
 * Use is subject to license and non-disclosure terms
 */

import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { AbstractResourceService, JsonApiListResourceMetadataBase, JsonApiResource } from '@irian-ro/abstract-resource';
import { Observable } from 'rxjs';
import {
  TaskDeleteStateVO,
  TaskDtoModel,
  TaskListWithRouteDTO,
  TaskListWithSolutionDtoModel,
  TaskListWithSolutionFromHubDtoModel,
  TaskViewVoModel
} from '@well/delivery-libs/task-management/domain';
import { EnvironmentService } from '@irian-ro/util-environment';
import { ShipmentRoutingDtoModel } from '@well/delivery-libs/shipment/domain';

@Injectable({
  providedIn: 'root',
})
export class TaskResourceService extends AbstractResourceService<TaskDtoModel, TaskDtoModel, unknown, unknown> {
  constructor(httpClient: HttpClient, environmentService: EnvironmentService) {
    super(httpClient, environmentService.getEnvironmentProperty('taskUrl'), TaskDtoModel, TaskDtoModel);
  }

  getTemplateFromRoutingSolution(
    routingSolutionUuid: string
  ): Observable<JsonApiResource<TaskListWithRouteDTO, unknown>> {
    return this.httpClient?.get(`${this.url}/template/${routingSolutionUuid}`) as Observable<
      JsonApiResource<TaskListWithRouteDTO, unknown>
    >;
  }

  // Per-leg task template (DELIWELL-4613): the backend renders the leg's single
  // routing solution into task(s), restricted to confirmed-order packages not
  // yet on a live task. Same TaskListWithRouteDTO shape as
  // getTemplateFromRoutingSolution.
  getTemplateFromSegmentDefinition(
    segmentDefinitionUuid: string
  ): Observable<JsonApiResource<TaskListWithRouteDTO, unknown>> {
    return this.httpClient?.get(`${this.url}/template/segment-definition/${segmentDefinitionUuid}`) as Observable<
      JsonApiResource<TaskListWithRouteDTO, unknown>
    >;
  }

  getTemplateFromHub(
    routedOrderTemplateUuid: string,
    shipmentDraftUuids: string[]
  ): Observable<JsonApiResource<TaskListWithSolutionFromHubDtoModel, unknown>> {
    const params = new HttpParams().set('shipmentDraftUuids', shipmentDraftUuids.join(','));

    return this.httpClient?.get(`${this.url}/template/hub/routed-order-template/${routedOrderTemplateUuid}`, { params }) as Observable<
      JsonApiResource<TaskListWithSolutionFromHubDtoModel, unknown>
    >;
  }

  getTemplateFromHubAdHoc(
    shipmentDraftUuids: string[]
  ): Observable<JsonApiResource<TaskListWithSolutionFromHubDtoModel, unknown>> {
    const params = new HttpParams().set('shipmentDraftUuids', shipmentDraftUuids.join(','));

    return this.httpClient?.get(`${this.url}/template/hub/ad-hoc`, { params }) as Observable<
      JsonApiResource<TaskListWithSolutionFromHubDtoModel, unknown>
    >;
  }

  getTemplateFromHubDraft(
    routedOrderTemplateDraftUuid: string
  ): Observable<JsonApiResource<TaskListWithSolutionFromHubDtoModel, unknown>> {
    return this.httpClient?.get(`${this.url}/template/hub/draft/${routedOrderTemplateDraftUuid}`) as Observable<
      JsonApiResource<TaskListWithSolutionFromHubDtoModel, unknown>
    >;
  }

  getTemplateFromShipmentGroup(spOrderUuid: string) {
    return this.httpClient?.get<JsonApiResource<TaskListWithSolutionDtoModel, unknown>>(
      `${this.url}/template/shipment-group/${spOrderUuid}`
    );
  }

  getTaskViewByUuid(uuid: string): Observable<JsonApiResource<TaskViewVoModel, unknown>> {
    return this.httpClient?.get(`${this.url}/view/${uuid}`) as Observable<
      JsonApiResource<TaskViewVoModel, unknown>
    >;
  }

  getTaskListForSegment(
    segmentUuid: string
  ): Observable<JsonApiResource<TaskViewVoModel[], JsonApiListResourceMetadataBase>> {
    return this.httpClient?.get(`${this.url}/segment/${segmentUuid}`) as Observable<
      JsonApiResource<TaskViewVoModel[], JsonApiListResourceMetadataBase>
    >;
  }

  saveTaskListWithRoute(
    taskListWithRouteDTO: TaskListWithRouteDTO
  ): Observable<JsonApiResource<TaskListWithRouteDTO, unknown>> {
    return this.httpClient
      ?.post(`${this.url}`, taskListWithRouteDTO, { headers: this.headers })
      .pipe(this.getDtoMapper());
  }

  saveFromHub(
    taskListWithSolutionFromHubDTO: TaskListWithSolutionFromHubDtoModel
  ): Observable<JsonApiResource<TaskListWithSolutionFromHubDtoModel, unknown>> {
    return this.httpClient
      ?.post(`${this.url}/hub`, taskListWithSolutionFromHubDTO, { headers: this.headers })
      .pipe(this.getDtoMapper());
  }

  saveFromShipmentRoutingSolution(
    shipmentRoutingDtoModel: ShipmentRoutingDtoModel,
    hasAssociatedTerminalReservations = false
  ) {
    let url = `${this.url}/shipment-routing-solution`;
    if (hasAssociatedTerminalReservations) {
      url += '?hasAssociatedTerminalReservations=true';
    }
    return this.httpClient
      ?.post(url, shipmentRoutingDtoModel, { headers: this.headers });
  }

  saveServiceProviderBehalfFromShipmentRoutingSolution(
    shipmentRoutingDtoModel: ShipmentRoutingDtoModel,
    hasAssociatedTerminalReservations = false,
    orderUuid: string
  ) {
    let url = `${this.url}/shipment-routing-solution/service-provider-behalf/${orderUuid}`;
    if (hasAssociatedTerminalReservations) {
      url += '?hasAssociatedTerminalReservations=true';
    }
    return this.httpClient
      ?.post(url, shipmentRoutingDtoModel, { headers: this.headers });
  }

  saveWithDefaultSolution(
    taskListWithSolutionDTO: TaskListWithSolutionDtoModel
  ): Observable<JsonApiResource<TaskListWithSolutionDtoModel, unknown>> {
    return this.httpClient
      ?.post(`${this.url}/default`, taskListWithSolutionDTO, { headers: this.headers })
      .pipe(this.getDtoMapper());
  }

  deleteSegment(uuid: string) {
    return this.httpClient?.delete(`${this.url}/${uuid}/segment`, { headers: this.headers });
  }

  deleteWithSolution(uuid: string) {
    return this.httpClient?.delete(`${this.url}/${uuid}/solution`, { headers: this.headers });
  }

  delete(uuid: string) {
    return this.httpClient?.delete(`${this.url}/${uuid}`, { headers: this.headers });
  }

  getDeleteState(uuid: string): Observable<TaskDeleteStateVO> {
    return this.httpClient?.get<TaskDeleteStateVO>(`${this.url}/${uuid}/delete-state`) as Observable<TaskDeleteStateVO>;
  }

  close(uuid: string) {
    return this.httpClient?.patch(`${this.url}/${uuid}/close`, {}, { headers: this.headers });
  }
}
